import DOMPurify from 'dompurify'
import { Branded } from 'types/generics/brand'

/**
 * Branded type for HTML string content from the rich text editor
 * This ensures we're handling sanitized HTML content safely
 */
export type RichTextContent = Branded<string, 'RichTextContent'>

/**
 * Editor mode enum for different rendering contexts
 */
export enum EditorMode {
  EDIT = 'edit',
  VIEW = 'view',
}

/**
 * Configuration options for the rich text editor
 */
export interface RichTextConfig {
  placeholder?: string
  readOnly?: boolean
  mode?: EditorMode
  autoFocus?: boolean
}

/**
 * Sanitizes HTML content using DOMPurify to prevent XSS attacks
 * Returns branded RichTextContent type
 */
export const sanitizeHtmlContent = (html: string): RichTextContent => {
  const sanitized = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p',
      'br',
      'strong',
      'em',
      'u',
      's',
      'h1',
      'h2',
      'h3',
      'ul',
      'ol',
      'li',
      'a',
      'span',
      'div',
      // Custom elements for embedded objects
      'embedded-object',
      'mention-user',
      'embedded-image',
    ],
    ALLOWED_ATTR: [
      'href',
      'target',
      'rel',
      // Custom attributes for embedded objects
      'data-type',
      'data-id',
      'data-name',
      'data-metadata',
      // Custom attributes for mentions
      'data-user-id',
      'data-user-name',
      // Custom attributes for images
      'data-entry-id',
      'data-image-path',
      'data-alt-text',
      'data-width',
      'data-height',
    ],
    KEEP_CONTENT: true,
  })

  return sanitized as RichTextContent
}

/**
 * Creates branded RichTextContent from raw HTML string
 * Use this when you trust the HTML content (e.g., from database)
 */
export const createRichTextContent = (html: string): RichTextContent => {
  return sanitizeHtmlContent(html)
}

/**
 * Extracts plain text from RichTextContent
 */
export const extractPlainText = (content: RichTextContent): string => {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = content
  return tempDiv.textContent || tempDiv.innerText || ''
}
